import { computePeaks, type FileMeta, type GeneratedFile } from '@tomo/core';
import { useEffect, useState } from 'react';
import { guessMime } from '../lib/files.ts';
import { db } from './db.ts';

/**
 * Content-addressed file storage. Every asset file is stored once, keyed by the SHA-256 of its
 * bytes; records only hold `FileMeta` (hash + metadata). Cloud projects register a remote source
 * so missing blobs are fetched on demand and uploads are deduplicated by hash.
 */

/* ------------------------------------------------------------------------------ hashing */

export async function sha256Hex(bytes: ArrayBuffer | Uint8Array): Promise<string> {
  const data = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  if (globalThis.crypto?.subtle) {
    const digest = await crypto.subtle.digest('SHA-256', data as BufferSource);
    return hex(new Uint8Array(digest));
  }
  return hex(sha256Fallback(data)); // non-secure contexts (e.g. http://192.168.x.x on a phone)
}

const hex = (b: Uint8Array) => Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');

function sha256Fallback(msg: Uint8Array): Uint8Array {
  const K = new Uint32Array([
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5, 0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3,
    0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
    0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3, 0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208,
    0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
  ]);
  const H = new Uint32Array([0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19]);
  const len = msg.length;
  const total = Math.ceil((len + 9) / 64) * 64;
  const buf = new Uint8Array(total);
  buf.set(msg);
  buf[len] = 0x80;
  const view = new DataView(buf.buffer);
  view.setUint32(total - 4, (len * 8) >>> 0);
  view.setUint32(total - 8, Math.floor((len * 8) / 2 ** 32));
  const W = new Uint32Array(64);
  const rotr = (x: number, n: number) => (x >>> n) | (x << (32 - n));
  for (let off = 0; off < total; off += 64) {
    for (let i = 0; i < 16; i++) W[i] = view.getUint32(off + i * 4);
    for (let i = 16; i < 64; i++) {
      const s0 = rotr(W[i - 15]!, 7) ^ rotr(W[i - 15]!, 18) ^ (W[i - 15]! >>> 3);
      const s1 = rotr(W[i - 2]!, 17) ^ rotr(W[i - 2]!, 19) ^ (W[i - 2]! >>> 10);
      W[i] = (W[i - 16]! + s0 + W[i - 7]! + s1) >>> 0;
    }
    let [a, b, c, d, e, f, g, h] = H as unknown as number[];
    for (let i = 0; i < 64; i++) {
      const S1 = rotr(e!, 6) ^ rotr(e!, 11) ^ rotr(e!, 25);
      const ch = (e! & f!) ^ (~e! & g!);
      const t1 = (h! + S1 + ch + K[i]! + W[i]!) >>> 0;
      const S0 = rotr(a!, 2) ^ rotr(a!, 13) ^ rotr(a!, 22);
      const maj = (a! & b!) ^ (a! & c!) ^ (b! & c!);
      const t2 = (S0 + maj) >>> 0;
      h = g;
      g = f;
      f = e;
      e = (d! + t1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (t1 + t2) >>> 0;
    }
    H[0] = (H[0]! + a!) >>> 0;
    H[1] = (H[1]! + b!) >>> 0;
    H[2] = (H[2]! + c!) >>> 0;
    H[3] = (H[3]! + d!) >>> 0;
    H[4] = (H[4]! + e!) >>> 0;
    H[5] = (H[5]! + f!) >>> 0;
    H[6] = (H[6]! + g!) >>> 0;
    H[7] = (H[7]! + h!) >>> 0;
  }
  const out = new Uint8Array(32);
  const ov = new DataView(out.buffer);
  H.forEach((v, i) => ov.setUint32(i * 4, v));
  return out;
}

/* ------------------------------------------------------------------------------ storage */

export async function putBlob(data: Blob | Uint8Array, mime: string): Promise<{ hash: string; size: number }> {
  const bytes = data instanceof Uint8Array ? data : new Uint8Array(await data.arrayBuffer());
  const hash = await sha256Hex(bytes);
  const d = await db();
  if (!(await d.getKey('blobs', hash))) {
    await d.put('blobs', { hash, blob: new Blob([bytes as BlobPart], { type: mime }), mime, size: bytes.byteLength, createdAt: Date.now() });
  }
  return { hash, size: bytes.byteLength };
}

export async function getBlob(hash: string): Promise<Blob | undefined> {
  return (await (await db()).get('blobs', hash))?.blob;
}

export async function hasBlob(hash: string): Promise<boolean> {
  return !!(await (await db()).getKey('blobs', hash));
}

/* ------------------------------------------------------------------------------ remote sources (cloud) */

type RemoteSource = (hash: string) => Promise<Blob | null>;
const remoteSources = new Map<string, RemoteSource>();

export function registerRemoteBlobSource(projectId: string, source: RemoteSource) {
  remoteSources.set(projectId, source);
  return () => {
    if (remoteSources.get(projectId) === source) remoteSources.delete(projectId);
  };
}

/* ------------------------------------------------------------------------------ object URLs */

const urls = new Map<string, string>();
const inflight = new Map<string, Promise<string | null>>();

export function cachedBlobUrl(hash: string | null | undefined): string | null {
  return hash ? (urls.get(hash) ?? null) : null;
}

export function blobUrl(hash: string, projectId?: string | null): Promise<string | null> {
  const hit = urls.get(hash);
  if (hit) return Promise.resolve(hit);
  let p = inflight.get(hash);
  if (!p) {
    p = (async () => {
      let blob = await getBlob(hash);
      if (!blob && projectId) {
        const remote = remoteSources.get(projectId);
        const fetched = remote ? await remote(hash).catch(() => null) : null;
        if (fetched) {
          await putBlob(fetched, fetched.type || 'application/octet-stream');
          blob = fetched;
        }
      }
      if (!blob) return null;
      const url = URL.createObjectURL(blob);
      urls.set(hash, url);
      return url;
    })().finally(() => inflight.delete(hash));
    inflight.set(hash, p);
  }
  return p;
}

/** Object URL for a stored blob (null while loading or when missing). */
export function useBlobUrl(hash: string | null | undefined, projectId?: string | null): string | null {
  const [url, setUrl] = useState<string | null>(() => cachedBlobUrl(hash));
  useEffect(() => {
    if (!hash) {
      setUrl(null);
      return;
    }
    const hit = cachedBlobUrl(hash);
    if (hit) {
      setUrl(hit);
      return;
    }
    let alive = true;
    setUrl(null);
    blobUrl(hash, projectId).then((u) => alive && setUrl(u));
    return () => {
      alive = false;
    };
  }, [hash, projectId]);
  return url;
}

/* ------------------------------------------------------------------------------ ingest (metadata, previews) */

const THUMB_MAX = 480;

async function canvasToBlob(canvas: HTMLCanvasElement, type = 'image/webp', quality = 0.84): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), type, quality));
}

async function loadImage(blob: Blob): Promise<HTMLImageElement> {
  const url = URL.createObjectURL(blob);
  try {
    const img = new Image();
    img.decoding = 'async';
    img.src = url;
    await img.decode();
    return img;
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}

async function makeThumb(source: CanvasImageSource, w: number, h: number): Promise<string | undefined> {
  const scale = Math.min(1, THUMB_MAX / Math.max(w, h));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(w * scale));
  canvas.height = Math.max(1, Math.round(h * scale));
  const ctx = canvas.getContext('2d');
  if (!ctx) return undefined;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
  const blob = await canvasToBlob(canvas);
  if (!blob) return undefined;
  return (await putBlob(blob, blob.type)).hash;
}

async function imageMeta(blob: Blob, mime: string): Promise<Partial<FileMeta>> {
  try {
    const img = await loadImage(blob);
    const width = img.naturalWidth;
    const height = img.naturalHeight;
    const raster = mime !== 'image/svg+xml' && mime !== 'image/gif';
    const thumb = raster && Math.max(width, height) > THUMB_MAX * 1.25 ? await makeThumb(img, width, height) : undefined;
    return { width, height, ...(thumb ? { thumb } : {}) };
  } catch {
    return {};
  }
}

async function videoMeta(blob: Blob): Promise<Partial<FileMeta>> {
  const url = URL.createObjectURL(blob);
  try {
    const video = document.createElement('video');
    video.muted = true;
    video.preload = 'metadata';
    video.src = url;
    await new Promise<void>((res, rej) => {
      video.onloadedmetadata = () => res();
      video.onerror = () => rej(new Error('video'));
    });
    const duration = Number.isFinite(video.duration) ? video.duration : undefined;
    video.currentTime = Math.min(1, (duration ?? 2) * 0.25);
    await new Promise<void>((res) => {
      video.onseeked = () => res();
      setTimeout(res, 2500);
    });
    const thumb = video.videoWidth ? await makeThumb(video, video.videoWidth, video.videoHeight) : undefined;
    return { width: video.videoWidth || undefined, height: video.videoHeight || undefined, duration, ...(thumb ? { thumb } : {}) };
  } catch {
    return {};
  } finally {
    URL.revokeObjectURL(url);
  }
}

async function audioMeta(blob: Blob): Promise<Partial<FileMeta>> {
  try {
    const ctx = new OfflineAudioContext(1, 1, 44100);
    const audio = await ctx.decodeAudioData(await blob.arrayBuffer());
    return { duration: audio.duration, peaks: computePeaks(audio.getChannelData(0), 120) };
  } catch {
    return {};
  }
}

/** Store a user file and extract everything previews need. */
export async function ingestFile(file: Blob, name = (file as File).name ?? 'file'): Promise<FileMeta> {
  const mime = file.type || guessMime(name);
  const { hash, size } = await putBlob(file, mime);
  const meta: FileMeta = { hash, name, mime, size };
  if (mime.startsWith('image/')) Object.assign(meta, await imageMeta(file, mime));
  else if (mime.startsWith('video/')) Object.assign(meta, await videoMeta(file));
  else if (mime.startsWith('audio/')) Object.assign(meta, await audioMeta(file));
  return meta;
}

/** Store a file generated by a template (metadata is already known). */
export async function ingestGenerated(file: GeneratedFile): Promise<FileMeta> {
  const { hash, size } = await putBlob(file.bytes, file.mime);
  const meta: FileMeta = { hash, name: file.name, mime: file.mime, size };
  if (file.width) meta.width = file.width;
  if (file.height) meta.height = file.height;
  if (file.duration) meta.duration = file.duration;
  if (file.peaks) meta.peaks = file.peaks;
  return meta;
}

/** Every blob hash referenced by a project's file fields (for export and cloud upload). */
export function collectFileHashes(files: Iterable<FileMeta>): string[] {
  const out = new Set<string>();
  for (const f of files) {
    out.add(f.hash);
    if (f.thumb) out.add(f.thumb);
  }
  return [...out];
}
